// ================= THE DAILY CHALLENGE =================
//
// deploy_handoff/accounts-stage2-scope.md §6. Three bouts a day against three named decks, worth
// 15 / 10 / 5 credits, once each.
//
// THE ONLY FILE IN js/ THAT KNOWS /api/daily EXISTS, in the same way js/credits.js is the only one
// that knows about /api/bouts. It holds the mode's rules and none of its rendering; js/ui.js asks
// it what to draw and what a bout should be set up as.
//
// WHAT MAKES THIS A MODE RATHER THAN A PRESET. Three things differ from an ordinary vs-AI bout and
// each one is a rule the other modes do not have:
//
//   THE OPPONENT DECK IS FIXED, by the day and the tier, not chosen.
//   THE SEED IS FIXED, and identical for every player in the world today (§6.2) — which is what
//     makes one player's Gold win comparable to another's, and is also why a persistent player will
//     eventually solve it. That was chosen knowingly and is bounded at 30 credits a day.
//   THE DAY'S THREE DECKS CANNOT BE PLAYED, so nobody beats Gold by bringing the Gold deck.
//
// OFFLINE IT SIMPLY DOES NOT EXIST. There is no local fallback and there should not be: a challenge
// computed on the client would be a challenge the client could choose, and the whole feature rests
// on everyone getting the same three decks.
window.DailyChallenge = (function () {
  'use strict';

  const TIERS = ['gold', 'silver', 'bronze'];
  const LABEL = { gold: 'Gold', silver: 'Silver', bronze: 'Bronze' };

  // Last answer from /api/daily. Null until asked, and re-read rather than trusted across a day
  // boundary — a tab left open overnight would otherwise offer yesterday's challenge, which the
  // server refuses with `wrong_day` and the player experiences as a bout that paid nothing.
  let STATE = null;

  async function getJson(url) {
    const res = await window.fetch(url, { credentials: 'same-origin' });
    // Content-type, not status — the trap js/deck-sync.js documents at length.
    const type = res.headers.get('Content-Type') || '';
    if (!/json/i.test(type)) throw new Error('api_unavailable');
    return { status: res.status, body: await res.json() };
  }

  // Asks the server for today. Returns null on every failure, because every caller's correct
  // response to "no challenge available" is to show nothing at all.
  async function load() {
    try {
      const { body } = await getJson('/api/daily');
      if (!body || !Array.isArray(body.challenges) || !body.challenges.length) return null;
      STATE = {
        day: body.day,
        signedIn: !!body.signedIn,
        beaten: Array.isArray(body.beaten) ? body.beaten : [],
        excluded: Array.isArray(body.excluded) ? body.excluded : [],
        remaining: typeof body.remaining === 'number' ? body.remaining : null,
        cap: body.cap || 0,
        // The day`s themed set: { id, name, tagline, palette }. Carried through untouched and
        // allowed to be absent -- a server that predates the rotation sends none, and the panel
        // renders without a theme row rather than with an empty one.
        set: body.set || null,
        challenges: body.challenges
      };
      return STATE;
    } catch (e) {
      STATE = null;
      return null;
    }
  }

  function state() { return STATE; }
  function challengeFor(tier) {
    if (!STATE) return null;
    return STATE.challenges.filter(c => c.tier === tier)[0] || null;
  }
  function isBeaten(tier) { return !!STATE && STATE.beaten.indexOf(tier) >= 0; }

  // Which of the player's decks they may bring today. Everything they have, minus the three on
  // offer.
  //
  // DERIVED FROM THE SERVER'S `excluded` LIST rather than from the three challenge deck ids, even
  // though today they are the same three. The rule belongs to the day, not to the podium: when the
  // challenge decks become authored ones a player could never select anyway, `excluded` empties and
  // nothing here changes.
  function selectableDecks(allIds) {
    const bar = (STATE && STATE.excluded) || [];
    return (allIds || []).filter(id => bar.indexOf(id) === -1);
  }

  // Everything js/ui.js needs to start the bout, or null if this tier cannot be played right now.
  // Deliberately does NOT decide the player's deck — that is the one choice the mode leaves open,
  // and the setup screen owns it.
  function boutSetup(tier, playerDeckId) {
    const c = challengeFor(tier);
    if (!c) return null;
    if (!playerDeckId) return null;
    if (((STATE && STATE.excluded) || []).indexOf(playerDeckId) >= 0) return null;
    return {
      tier: tier,
      label: LABEL[tier],
      day: STATE.day,
      seed: c.seed,
      award: c.award,
      playerDeckId: playerDeckId,
      opponentDeckId: c.deckId,
      // Already beaten is NOT a reason to refuse the bout. Retries are unlimited and a player may
      // want to fight Gold again for its own sake; what it changes is only whether it pays.
      paysOut: !isBeaten(tier),
      // HOW MUCH ROOM THE DAY HAS LEFT, carried so the result screen can promise a number the
      // ledger will honour. It is the server's figure, from /api/daily, and never computed here --
      // the day and its total belong to the server's clock.
      //
      // NULL IS A REAL ANSWER and means "do not clamp": a deployment that predates the field sends
      // none, and a promise clamped by a number nobody sent would be worse than an unclamped one.
      // It can also be STALE, if the player won a bout on another device since this was loaded --
      // which is why it only shapes the expectation, and the settled amount from /api/bouts is what
      // the screen ends up showing.
      remaining: (STATE && typeof STATE.remaining === 'number') ? STATE.remaining : null
    };
  }

  // What today is worth to this player, for the panel's summary line.
  function summary() {
    if (!STATE) return null;
    const total = STATE.challenges.reduce((n, c) => n + (c.award || 0), 0);
    const won = STATE.challenges
      .filter(c => isBeaten(c.tier))
      .reduce((n, c) => n + (c.award || 0), 0);
    return { total, won, left: total - won, allBeaten: won === total && total > 0 };
  }

  return {
    TIERS, LABEL,
    load, state, challengeFor, isBeaten, selectableDecks, boutSetup, summary,
    _setState: (s) => { STATE = s; }
  };
})();
